/**
 * CR500: Divider Block
 * 
 * Visual separator between sections.
 * Makes dividers/spacers configurable via Layout Studio,
 * eliminating hardcoded borders/spacing in the public page.
 * 
 * Config options:
 * - variant: "line" | "space" - Visual style
 * - spacing: "sm" | "md" | "lg" - Amount of spacing
 */

import type { BlockProps } from "./types";
import type { DividerBlockConfig } from "@/lib/layout-studio/types";
import { DEFAULT_DIVIDER_CONFIG } from "@/lib/layout-studio/types";
import { cn } from "@/lib/utils";

const SPACING_CLASSES = {
  sm: "py-2",
  md: "py-4",
  lg: "py-8",
} as const;

export function DividerBlock({ config }: BlockProps<DividerBlockConfig>) {
  // Merge with defaults
  const mergedConfig: DividerBlockConfig = {
    ...DEFAULT_DIVIDER_CONFIG,
    ...config,
  };

  const spacingClass = SPACING_CLASSES[mergedConfig.spacing] || SPACING_CLASSES.md;

  if (mergedConfig.variant === "space") {
    // Space-only divider (no visual line)
    return <div className={spacingClass} aria-hidden="true" />;
  }

  // Line divider
  return (
    <div className={cn(spacingClass, "flex items-center")} aria-hidden="true">
      <div className="flex-1 border-t border-border" />
    </div>
  );
}
